import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { message } from 'antd';

import { Rides } from '../..';
import { useRedeemFlexibleRideModal, useConfigureBillingModal } from '../../../components';
import { renderColumns } from './renderColumns';
import { renderTableHead } from './renderTableHead';
import { copyRide } from '../../../store/drafts';
import { getCustomUserFilter, saveCustomUserFilter, setMyFilter, setFilteredStatus, redeemFlexibleUberRide, fetchRides } from '../../../store/rides';
import { useEditReccuringModal } from '../../../hooks/useEditRecurringModal';

import styles from './index.module.css';
import { Navigation } from '../../../utils/navigation';
import { UBER_TEXT, URLS, USER_ROLES } from '../../../constants/constants';
import { buildUberNeedAttentionListFilter } from '../../../utils/helpers';
import { fetchAllHealthOrganizations } from '../../../store/health_organizations';
import { clearNeedsBillingSetup } from '../../../store/authorization';

const HO_Rides = ({ copyRide, getCustomUserFilter, savedFilterParams,
  hasSavedFilterParams, healthOrganizations, fetchAllHealthOrganizations, setMyFilter, setFilteredStatus, redeemFlexibleUberRide, fetchRides, clearNeedsBillingSetup }) => {
  const navigate = Navigation();
  const { EditRecurringModal, handleEditRide, handleRescheduleRide } = useEditReccuringModal();
  const [selectedRedeemRide, setSelectedRedeemRide] = useState(null);
  const [redeemLoading, setRedeemLoading] = useState(false);

  const user = useSelector((state) => state.authorization.user);
  const isViewDashboardMode = useSelector(
    (state) => state.authorization?.viewDashboardMode?.active,
  );
  const needsBillingSetup = useSelector(
    (state) => state.authorization?.viewDashboardMode?.needsBillingSetup,
  );

  const handleBillingRequiredOk = useCallback(() => {
    clearNeedsBillingSetup();
    navigate.push(URLS.PREFERENCES);
  }, [clearNeedsBillingSetup, navigate]);

  const handleBillingRequiredCancel = useCallback(() => {
    clearNeedsBillingSetup();
  }, [clearNeedsBillingSetup]);

  const {
    Modal: BillingRequiredModal,
    setConfigureBillingModalVisible,
  } = useConfigureBillingModal({
    title: 'Action Required',
    description: 'Billing must be configured for this organization to enable ride scheduling.',
    onConfirm: handleBillingRequiredOk,
    onCancel: handleBillingRequiredCancel,
  });

  useEffect(() => {
    if (isViewDashboardMode && needsBillingSetup) {
      setConfigureBillingModalVisible(true);
    }
  }, [isViewDashboardMode, needsBillingSetup, setConfigureBillingModalVisible]);

  const isUberEligible = useSelector((state) => state.authorization.isUberEligible);

  const showNeedAttentionUberHo = Boolean(isUberEligible);
  const uberNeedAttentionRideIds = useSelector((state) => state.rides.uberNeedAttentionRideIds);

  const setNeedAttentionFilter = useCallback(() => {
    setMyFilter(buildUberNeedAttentionListFilter(uberNeedAttentionRideIds));
    setFilteredStatus(true);
  }, [setMyFilter, setFilteredStatus, uberNeedAttentionRideIds]);

  const handleCopyRide = useCallback((ride) => {
    if (ride?.connectedLegId) {
      return copyRide(ride?.leg.toLowerCase() === 'a' ? ride.id : ride?.connectedLegId, navigate)
    }

    return copyRide(ride?.id, navigate)

  }, [copyRide, navigate]);

  const handleRequestNewUberRide = useCallback(
    (id) => {
      if (!id) return;
      navigate.push(URLS.UBER_RESCHEDULE_RIDE.replace(':id', id));
    },
    [navigate],
  );

  const handleRedeemFlexibleRide = useCallback(async () => {
    if (!selectedRedeemRide?.id) return;

    setRedeemLoading(true);
    try {
      await redeemFlexibleUberRide({ rideId: selectedRedeemRide.id, Navigation: navigate });
      message.success(UBER_TEXT.REDEEM_RIDE_SUCCESS);
      fetchRides(false, navigate);
    } catch (error) {
      const data = error?.response?.data;
      const errorMessage =
        (typeof data === 'string' ? data : data?.message) ||
        UBER_TEXT.REDEEM_RIDE_ALREADY_REDEEMED;
      message.error(errorMessage);
      throw error;
    } finally {
      setRedeemLoading(false);
    }
  }, [selectedRedeemRide, navigate, redeemFlexibleUberRide, fetchRides]);

  const { Modal: RedeemModal, setRedeemModalVisible } = useRedeemFlexibleRideModal({
    id: selectedRedeemRide?.seqId,
    onConfirm: handleRedeemFlexibleRide,
    loading: redeemLoading,
  });

  const handleOpenRedeemModal = useCallback(
    (ride) => {
      setSelectedRedeemRide(ride);
      setRedeemModalVisible(true);
    },
    [setRedeemModalVisible],
  );

  const tableColumns = useMemo(
    () =>
      renderColumns({
        handleCopyRide,
        handleEditRide,
        handleRescheduleRide,
        handleRequestNewUberRide,
        handleOpenRedeemModal,
        navigate,
        user,
        isViewDashboardMode,
      }),
    [handleCopyRide, handleEditRide, handleRescheduleRide, handleRequestNewUberRide, handleOpenRedeemModal, navigate, user, isViewDashboardMode],
  );

  const tableHead = useMemo(() => renderTableHead({ navigate, healthOrganizations, user }), [navigate, healthOrganizations, user]);

  const ridesTable = useMemo(
    () => (
      <div className={styles.table}>
        <Rides
          columns={tableColumns}
          savedFilterParams={savedFilterParams}
          hasSavedFilterParams={hasSavedFilterParams}
          renderTableHead={tableHead}
          showNeedAttentionBtn={showNeedAttentionUberHo}
          setNeedAttentionFilter={setNeedAttentionFilter}
        />
      </div>
    ),
    [hasSavedFilterParams, tableHead, tableColumns, savedFilterParams],
  );

  useEffect(() => {
    if (user.permissions?.orgList) {
      fetchAllHealthOrganizations();
    }
    
    getCustomUserFilter();
     return;
  }, [getCustomUserFilter, fetchAllHealthOrganizations]);
  
  return (
    <>
      {ridesTable}
      {EditRecurringModal}
      {RedeemModal}
      {BillingRequiredModal}
    </>
  );
};

const mapStateToProps = (state) => ({
  healthOrganizations: state.healthOrganizations.healthOrganizations,
  savedFilterParams: state.rides?.savedFilterParams,
  hasSavedFilterParams: state.rides?.savedFilterParams && Boolean(Object.keys(state.rides?.savedFilterParams)?.length),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchAllHealthOrganizations,
      copyRide,
      getCustomUserFilter,
      saveCustomUserFilter,
      setMyFilter,
      setFilteredStatus,
      redeemFlexibleUberRide,
      fetchRides,
      clearNeedsBillingSetup,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(HO_Rides);
